import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../contexts/UserContext';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import { updatePassword } from '../services/api';
import AnimatedCloseButton from './AnimatedCloseButton';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (error: string) => void;
}

interface PasswordData {
  newPassword: string;
  confirmPassword: string;
}

export default function PasswordChangeModal({ isOpen, onClose, onSuccess, onError }: PasswordChangeModalProps) {
  const { userData } = useUser();

  // Scroll engelleyici hook kullan
  useScrollPrevention(isOpen);

  const [passwordData, setPasswordData] = useState<PasswordData>({
    newPassword: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Modal geri düğmesi handler
  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);
  
  useModalBackHandler({ 
    isOpen, 
    onClose: handleModalClose, 
    enabled: true
  });

  // Modal açıldığında formu temizle
  useEffect(() => {
    if (isOpen) {
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
      setErrors({});
      setLoading(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: { [key: string]: string | undefined } = {};
    let errorMessage = '';

    // Yeni parola kontrolü
    if (!passwordData.newPassword.trim()) {
      newErrors.newPassword = 'error';
      errorMessage = 'Yeni parolanızı giriniz.';
    } else if (passwordData.newPassword.length < 6) {
      newErrors.newPassword = 'error';
      errorMessage = 'Parola en az 6 karakter olmalıdır.';
    }

    // Parola tekrarı kontrolü
    if (!passwordData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'error';
      if (!errorMessage) errorMessage = 'Parola tekrarını giriniz.';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'error';
      if (!errorMessage) errorMessage = 'Parolalar eşleşmiyor.';
    }

    setErrors(newErrors);
    
    // Hata varsa toast göster
    if (Object.keys(newErrors).length > 0) {
      onError(errorMessage);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);

      try {
        const result = await updatePassword(passwordData.newPassword);

        if (result.success) {
          onSuccess('Parolanız başarıyla değiştirildi.');
          handleClose();
        } else {
          onError(result.error || 'Parola değiştirme işlemi başarısız.');
        }
      } catch (error) {
        console.error('Parola değiştirme hatası:', error);
        onError('Parola değiştirme sırasında bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClose = () => {
    setPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
    setLoading(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const handleInputChange = (field: keyof PasswordData, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));

    // Kullanıcı yazmaya başladığında sadece o alanın hatasını temizle
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-y-auto bg-black/20 dark:bg-black/40 backdrop-blur-sm"
            onClick={handleBackdropClick}
          >
            {/* Modal */}
            <div className="flex min-h-full items-center justify-center px-2 py-4 text-center sm:p-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative transform transition-transform overflow-visible rounded-xl backdrop-blur-xs bg-gradient-to-br from-white/90 via-white/88 to-white/85 dark:from-gray-700/85 dark:via-gray-750/83 dark:to-gray-800/85 border border-white/50 dark:border-gray-600/50 text-left shadow-2xl shadow-black/10 dark:shadow-black/30 w-full mx-2 sm:mx-0 sm:my-8 sm:w-full sm:max-w-md"
                role="dialog"
                aria-modal="true"
              >
                {/* Header */}
                <div className="border-b border-gray-300 dark:border-gray-600 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Parola Değiştir
                    </h3>
                    <AnimatedCloseButton 
                      onClick={handleClose}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="px-6 pt-4 pb-6 sm:p-8">
                  <div className="space-y-4">
                    {/* Yeni Parola */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Yeni Parola <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) => handleInputChange('newPassword', e.target.value)}
                          className={errors.newPassword ? 'form-input-error w-full pr-10' : 'form-input w-full pr-10 bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                          placeholder="Yeni parolanızı girin"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showNewPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3l18 18" />
                              <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                            </svg>
                          )}
                      </button>
                    </div>
                  </div>

                    {/* Parola Tekrarı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Parola Tekrarı <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          className={errors.confirmPassword ? 'form-input-error w-full pr-10' : 'form-input w-full pr-10 bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:border-gray-400 dark:focus:border-gray-500'}
                          placeholder="Yeni parolanızı tekrar girin"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showConfirmPassword ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3l18 18" />
                              <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                            </svg>
                          )}
                      </button>
                    </div>
                  </div>
                  </div>
                </form>

                {/* Footer */}
                <div className="border-t border-gray-300 dark:border-gray-600 px-6 py-4">
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={loading}
                      className="border border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-50 hover:via-gray-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                    >
                      <div className="rounded-lg border border-white dark:border-gray-700 px-5 py-2 text-base font-medium text-gray-700 dark:text-gray-300">
                        İptal
                      </div>
                    </button>
                    <button
                      type="submit"
                      onClick={handleSubmit}
                      disabled={loading}
                      className="px-5 py-2 text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-lg shadow-lg shadow-blue-500/25 transform transition-all duration-200 hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700"
                    >
                      {loading && (
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      )}
                      Değiştir
                    </button>
                  </div>
                </div>
            </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
